import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExpensePackButton,
  ReceiptPreview,
  ReceiptUploader,
} from '../components';
import { api } from '../lib/api';
import type {
  AddOrderRequest,
  OrderFormData,
  Receipt,
  Run,
  RunSummary,
} from '../types';

export const RunDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<Run | null>(null);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'orders' | 'summary' | 'receipt'>(
    'orders'
  );
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  const [orderForm, setOrderForm] = useState<OrderFormData>({
    customerName: '',
    customerEmail: '',
    items: [
      {
        name: '',
        size: '',
        customizations: [],
        price: 0,
        quantity: 1,
      },
    ],
    specialInstructions: '',
  });

  useEffect(() => {
    const fetchRunDetails = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [runResult, summaryResult] = await Promise.all([
          api.safeCall(() => api.getRun(id)),
          api.safeCall(() => api.getRunSummary(id)),
        ]);

        if (runResult.error) {
          setError(runResult.error);
          return;
        }

        if (summaryResult.error) {
          console.warn('Failed to load summary:', summaryResult.error);
        }

        setRun(runResult.data || null);
        setSummary(summaryResult.data || null);

        // Load receipt if it exists
        if (runResult.data?.receipt) {
          setReceipt(runResult.data.receipt);
        } else if (runResult.data?.id) {
          // Try to fetch receipt separately
          const receiptResult = await api.safeGetReceipt(runResult.data.id);
          if (receiptResult.data) {
            setReceipt(receiptResult.data);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load run');
      } finally {
        setLoading(false);
      }
    };

    fetchRunDetails();
  }, [id]);

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !orderForm.customerName.trim() || orderForm.items.length === 0)
      return;

    try {
      setSubmittingOrder(true);

      const orderRequest: AddOrderRequest = {
        customerName: orderForm.customerName.trim(),
        customerEmail: orderForm.customerEmail.trim() || undefined,
        items: orderForm.items
          .filter(item => item.name.trim() && item.price > 0)
          .map(item => ({
            ...item,
            id: crypto.randomUUID(),
          })),
        specialInstructions: orderForm.specialInstructions.trim() || undefined,
      };

      const result = await api.safeCall(() => api.addOrder(id, orderRequest));

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.data) {
        setRun(result.data);
        setShowOrderForm(false);
        setOrderForm({
          customerName: '',
          customerEmail: '',
          items: [
            {
              name: '',
              size: '',
              customizations: [],
              price: 0,
              quantity: 1,
            },
          ],
          specialInstructions: '',
        });

        // Refresh summary
        const summaryResult = await api.safeCall(() => api.getRunSummary(id));
        if (summaryResult.data) {
          setSummary(summaryResult.data);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add order');
    } finally {
      setSubmittingOrder(false);
    }
  };

  const handleAddItem = () => {
    setOrderForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: '',
          size: '',
          customizations: [],
          price: 0,
          quantity: 1,
        },
      ],
    }));
  };

  const handleRemoveItem = (index: number) => {
    if (orderForm.items.length > 1) {
      setOrderForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const copyShareLink = () => {
    if (run) {
      const shareUrl = `${window.location.origin}/join/${run.shareCode}`;
      navigator.clipboard.writeText(shareUrl);
      // TODO: Show toast notification
      alert('Share link copied to clipboard!');
    }
  };

  const handleReceiptUploaded = (newReceipt: Receipt) => {
    setReceipt(newReceipt);
    if (run) {
      setRun({ ...run, receipt: newReceipt });
    }
  };

  const handleReceiptUpdated = async (updatedReceipt: Receipt) => {
    if (!id) return;

    try {
      setReceipt(updatedReceipt);
      if (run) {
        setRun({ ...run, receipt: updatedReceipt });
      }
    } catch (error) {
      console.error('Failed to update receipt:', error);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading run details...</p>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❓</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Run not found'}
          </h2>
          <p className="text-gray-600 mb-6">
            The run you're looking for doesn't exist or couldn't be loaded.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            Back to Runs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{run.title}</h1>
              <p className="text-gray-600">Created by {run.createdBy}</p>
              <p className="text-sm text-gray-500">
                Created {formatDateTime(run.createdAt)}
              </p>
            </div>
            <div className="text-right">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  run.status === 'Open'
                    ? 'bg-green-100 text-green-800'
                    : run.status === 'Collecting'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                }`}
              >
                {run.status}
              </span>
              <p className="text-sm text-gray-500 mt-1">
                Share: {run.shareCode}
              </p>
            </div>
          </div>

          {run.description && (
            <p className="text-gray-700 mb-4">{run.description}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {run.pickupTime && (
              <div>
                <span className="font-medium text-gray-700">Pickup Time:</span>
                <p className="text-gray-900">
                  {formatDateTime(run.pickupTime)}
                </p>
              </div>
            )}
            {run.pickupLocation && (
              <div>
                <span className="font-medium text-gray-700">
                  Pickup Location:
                </span>
                <p className="text-gray-900">{run.pickupLocation}</p>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">Orders:</span>
              <p className="text-gray-900">
                {run.orderCount} (${run.totalAmount.toFixed(2)})
              </p>
            </div>
          </div>

          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setShowOrderForm(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Add Order
            </button>
            <button
              onClick={copyShareLink}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Copy Share Link
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'orders', label: 'Orders' },
                { key: 'summary', label: 'Summary' },
                { key: 'receipt', label: 'Receipt & Expenses' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'orders' && (
              <div className="space-y-6">
                {showOrderForm && (
                  <form
                    onSubmit={handleSubmitOrder}
                    className="bg-gray-50 rounded-lg p-6 space-y-4"
                  >
                    <h3 className="text-lg font-medium text-gray-900">
                      Add New Order
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Customer Name *
                        </label>
                        <input
                          type="text"
                          value={orderForm.customerName}
                          onChange={e =>
                            setOrderForm(prev => ({
                              ...prev,
                              customerName: e.target.value,
                            }))
                          }
                          required
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email (optional)
                        </label>
                        <input
                          type="email"
                          value={orderForm.customerEmail}
                          onChange={e =>
                            setOrderForm(prev => ({
                              ...prev,
                              customerEmail: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Items
                      </label>
                      {orderForm.items.map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2 p-4 border rounded"
                        >
                          <input
                            type="text"
                            placeholder="Item name"
                            value={item.name}
                            onChange={e =>
                              handleItemChange(index, 'name', e.target.value)
                            }
                            className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Size"
                            value={item.size}
                            onChange={e =>
                              handleItemChange(index, 'size', e.target.value)
                            }
                            className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <input
                            type="number"
                            placeholder="Price"
                            value={item.price}
                            onChange={e =>
                              handleItemChange(
                                index,
                                'price',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            step="0.01"
                            min="0"
                            className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={e =>
                              handleItemChange(
                                index,
                                'quantity',
                                parseInt(e.target.value) || 1
                              )
                            }
                            min="1"
                            className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            disabled={orderForm.items.length === 1}
                            className="text-red-600 hover:text-red-800 disabled:text-gray-400"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddItem}
                        className="text-indigo-600 hover:text-indigo-800 text-sm"
                      >
                        + Add Item
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Special Instructions
                      </label>
                      <textarea
                        value={orderForm.specialInstructions}
                        onChange={e =>
                          setOrderForm(prev => ({
                            ...prev,
                            specialInstructions: e.target.value,
                          }))
                        }
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowOrderForm(false)}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submittingOrder}
                        className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {submittingOrder ? 'Adding...' : 'Add Order'}
                      </button>
                    </div>
                  </form>
                )}

                {run.orders.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">
                      No orders yet. Be the first to add one!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {run.orders.map(order => (
                      <div
                        key={order.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {order.customerName}
                            </h4>
                            {order.customerEmail && (
                              <p className="text-sm text-gray-600">
                                {order.customerEmail}
                              </p>
                            )}
                            <div className="mt-2">
                              {order.items.map(item => (
                                <div key={item.id} className="text-sm">
                                  {item.quantity}x {item.name}
                                  {item.size && ` (${item.size})`} - $
                                  {item.price.toFixed(2)}
                                  {item.customizations.length > 0 && (
                                    <span className="text-gray-500">
                                      {' '}
                                      + {item.customizations.join(', ')}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                            {order.specialInstructions && (
                              <p className="text-sm text-gray-600 mt-1">
                                Note: {order.specialInstructions}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-medium">
                              ${order.amount.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {order.status}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'summary' && summary && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Total Orders
                    </h3>
                    <p className="text-3xl font-bold text-indigo-600">
                      {summary.orderCount}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Total Amount
                    </h3>
                    <p className="text-3xl font-bold text-green-600">
                      ${summary.totalAmount.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Status
                    </h3>
                    <p className="text-xl font-semibold text-gray-900">
                      {summary.status}
                    </p>
                  </div>
                </div>

                {summary.groupedItems.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Grouped Items
                    </h3>
                    <div className="space-y-2">
                      {summary.groupedItems.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                        >
                          <span className="font-medium">
                            {item.quantity}x {item.name}
                            {item.size && ` (${item.size})`}
                          </span>
                          <span className="text-gray-600">
                            ${item.totalPrice.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'receipt' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Receipt Upload/Preview */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Receipt Management
                    </h3>

                    {!receipt ? (
                      <ReceiptUploader
                        runId={run?.id || ''}
                        onReceiptUploaded={handleReceiptUploaded}
                        disabled={!run?.id}
                      />
                    ) : (
                      <ReceiptPreview
                        receipt={receipt}
                        onUpdate={handleReceiptUpdated}
                        editable={true}
                      />
                    )}
                  </div>

                  {/* Expense Pack Generation */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Expense Reporting
                    </h3>

                    <ExpensePackButton
                      run={run!}
                      receipt={receipt || undefined}
                      disabled={!run}
                    />

                    {receipt && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <h4 className="font-medium text-green-800 mb-2">
                          Receipt Information
                        </h4>
                        <div className="text-sm text-green-700 space-y-1">
                          <p>
                            <strong>Vendor:</strong> {receipt.vendor}
                          </p>
                          <p>
                            <strong>Total:</strong> $
                            {receipt.total?.toFixed(2) || '0.00'}
                          </p>
                          <p>
                            <strong>Date:</strong>{' '}
                            {receipt.date
                              ? new Date(receipt.date).toLocaleDateString()
                              : 'No date'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunDetail;
